import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, map } from 'rxjs';

export interface ApiResponse<T> {
  codigo: number;
  estado?: boolean;
  mensaje?: string;
  respuesta?: T;
}

export type ResourceName =
  | 'inspecciones'
  | 'mantenimientos'
  | 'fallas'
  | 'activos'
  | 'pqrs'
  | 'vehiculos'
  | 'checklists'
  | 'bpm-personal'
  | 'chequeos-bpm-personal'
  | 'control-proceso'
  | 'programas'
  | 'empresas'
  | 'sectores'
  | 'usuarios'
  | 'areas'
  | 'tipos-inspeccion'
  | 'tipos-vehiculo'
  | 'tipos-bpm-personal'
  | 'roles';

export interface ProgramUpload {
  tipo: string;
  nombre: string;
  idSector: number;
  idEmpresa: number;
  idProgramas?: number;
  archivo: File;
}

@Injectable({ providedIn: 'root' })
export class ApiService {
  constructor(private readonly http: HttpClient) {}

  getDashboard(params?: Record<string, string | number | null | undefined>): Observable<unknown> {
    return this.unwrap(this.http.get<ApiResponse<unknown>>('/webapi/dashboard/resumen', {
      params: this.toParams(params)
    }));
  }

  list(resource: ResourceName, params?: Record<string, string | number | null | undefined>): Observable<unknown[]> {
    return this.unwrap(this.http.get<ApiResponse<unknown[]>>(`/webapi/${resource}`, {
      params: this.toParams(params)
    })).pipe(map((value) => Array.isArray(value) ? value : []));
  }

  empresasPorSector(idSector: number): Observable<unknown[]> {
    return this.unwrap(this.http.get<ApiResponse<unknown[]>>(`/webapi/empresas/por-sector/${idSector}`))
      .pipe(map((value) => Array.isArray(value) ? value : []));
  }

  tipoVehiculo(id: string | number): Observable<Record<string, unknown>> {
    return this.unwrap(this.http.get<ApiResponse<Record<string, unknown>>>(`/webapi/tipos-vehiculo/${id}`));
  }

  tipoBpmPersonal(id: string | number): Observable<Record<string, unknown>> {
    return this.unwrap(this.http.get<ApiResponse<Record<string, unknown>>>(`/webapi/tipos-bpm-personal/${id}`));
  }

  checklistDetalles(id: string | number, idEmpresa?: number): Observable<unknown[]> {
    return this.unwrap(this.http.get<ApiResponse<unknown[]>>(`/webapi/checklists/${id}/detalles`, {
      params: this.toParams({ idEmpresa })
    })).pipe(map((value) => Array.isArray(value) ? value : []));
  }

  chequeoBpmPersonalDetalles(id: string | number, idEmpresa?: number): Observable<unknown[]> {
    return this.unwrap(this.http.get<ApiResponse<unknown[]>>(`/webapi/chequeos-bpm-personal/${id}/detalles`, {
      params: this.toParams({ idEmpresa })
    })).pipe(map((value) => Array.isArray(value) ? value : []));
  }

  create(resource: ResourceName, body: Record<string, unknown>): Observable<unknown> {
    return this.unwrap(this.http.post<ApiResponse<unknown>>(`/webapi/${resource}`, body));
  }

  update(resource: ResourceName, id: string | number, body: Record<string, unknown>): Observable<unknown> {
    return this.unwrap(this.http.put<ApiResponse<unknown>>(`/webapi/${resource}/${id}`, body));
  }

  delete(resource: ResourceName, id: string | number): Observable<unknown> {
    return this.unwrap(this.http.delete<ApiResponse<unknown>>(`/webapi/${resource}/${id}`));
  }

  uploadProgram(data: ProgramUpload): Observable<unknown> {
    const form = new FormData();
    form.append('nombre', data.nombre);
    form.append('idSector', String(data.idSector));
    form.append('idEmpresa', String(data.idEmpresa));
    form.append('idProgramas', String(data.idProgramas ?? 0));
    form.append('archivo', data.archivo, data.archivo.name);
    const endpoint = data.tipo === 'programas'
      ? '/webapi/programas/subir'
      : `/webapi/documentos/${data.tipo}/subir`;
    return this.unwrap(this.http.post<ApiResponse<unknown>>(endpoint, form));
  }

  listDocuments(tipo: string, params?: Record<string, string | number | null | undefined>): Observable<unknown[]> {
    return this.unwrap(this.http.get<ApiResponse<unknown[]>>(`/webapi/documentos/${tipo}`, {
      params: this.toParams(params)
    })).pipe(map((value) => Array.isArray(value) ? value : []));
  }

  deleteDocument(tipo: string, id: string | number): Observable<unknown> {
    return this.unwrap(this.http.delete<ApiResponse<unknown>>(`/webapi/documentos/${tipo}/${id}`));
  }

  setProgramVigente(id: string | number, vigente: boolean): Observable<unknown> {
    return this.unwrap(this.http.put<ApiResponse<unknown>>(`/webapi/programas/${id}/vigente`, { vigente }));
  }

  deleteProgram(id: string | number): Observable<unknown> {
    return this.unwrap(this.http.delete<ApiResponse<unknown>>(`/webapi/programas/${id}`));
  }

  pdfUrl(tipo: string, urlRelativa: string): string {
    const fileName = (urlRelativa || '').split('/').filter(Boolean).pop() ?? urlRelativa;
    return `/webapi/pdf/${tipo}/${encodeURIComponent(fileName)}`;
  }

  fotoUrl(urlRelativa: string): string {
    const fileName = (urlRelativa || '').split('/').filter(Boolean).pop() ?? urlRelativa;
    return `/webapi/foto/${encodeURIComponent(fileName)}`;
  }

  private unwrap<T>(request: Observable<ApiResponse<T>>): Observable<T> {
    return request.pipe(map((response) => {
      if (response.codigo !== 1) {
        throw new Error(response.mensaje ?? 'La API no devolvio una respuesta valida.');
      }

      return response.respuesta as T;
    }));
  }

  private toParams(values?: Record<string, string | number | null | undefined>): HttpParams {
    let params = new HttpParams();
    Object.entries(values ?? {}).forEach(([key, value]) => {
      if (value !== null && value !== undefined && value !== '') {
        params = params.set(key, String(value));
      }
    });
    return params;
  }
}
